import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import { ChestRewardsTooltip } from 'src/game/components/ChestRewardTooltip';
import { ProgressBar } from 'src/game/components/shared/ProgressBar';
import { Feature } from 'src/replicant/analytics';
import {
  getEventCompletedMilestones,
  getEventMilestones,
  getTreasureExpressRewards,
  getOngoingTreasureExpressEvent,
  getUnclaimedMilestones,
} from 'src/replicant/treasureExpress/treasureExpress.getters';
import { user, now } from 'src/StateObserver';
import { TreasureExpressChest } from './TreasureExpressChest';

export default class TreasureExpressProgressBar extends ProgressBar {
  public panelWood!: ChestRewardsTooltip;
  public panelSilver!: ChestRewardsTooltip;
  public panelGold!: ChestRewardsTooltip;

  constructor({
    superview,
    width = 380,
    height = 97,
    x,
    y,
    scale = 1,
    canHandleEvents = true,
    eventId,
  }: {
    superview: View;
    width?: number;
    height?: number;
    x?: number;
    y?: number;
    scale?: number;
    canHandleEvents?: boolean;
    eventId: string;
  }) {
    super({
      superview,
      bgStyle: {
        image: 'assets/ui/treasureExpress/popup/progress_bg.png',
        width,
        height,
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: { left: 90, right: 90 },
        },
        canHandleEvents,
      },
      fillStyle: {
        image: 'assets/ui/treasureExpress/popup/progress_fill.png',
        width: 0,
        height: height * (60 / 97),
        x: 18,
        y: 12,
        paddingRight: 18 * 2,
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: { left: 80, right: 80 },
        },
      },
    });

    this.updateOpts({
      width,
      height,
      x,
      y,
      scale,
    });

    this.paddingRight = 90;

    const event = user().treasureExpress?.entries[eventId];
    if (!event) return;

    const rewards = getTreasureExpressRewards(user(), eventId);

    new ImageScaleView({
      superview: this,
      centerOnOrigin: true,
      centerAnchor: true,
      scaleMethod: 'contain',
      canHandleEvents: false,
      image: 'assets/ui/treasureExpress/shared/ticket.png',
      width: 276,
      height: 257,
      scale: 0.5,
      x: 40,
      y: 40,
    });

    const wood = new TreasureExpressChest({
      superview: this,
      chestType: 'wood',
      centerOnOrigin: true,
      centerAnchor: true,
      canHandleEvents: true,
      width: 327,
      height: 327,
      scale: 0.46,
      x: 220,
      y: 30,
      zIndex: 1,
    });

    const silver = new TreasureExpressChest({
      superview: this,
      chestType: 'silver',
      centerOnOrigin: true,
      centerAnchor: true,
      canHandleEvents: true,
      width: 327,
      height: 327,
      scale: 0.46,
      x: 410,
      y: 30,
      zIndex: 1,
    });

    const gold = new TreasureExpressChest({
      superview: this,
      chestType: 'gold',
      centerOnOrigin: true,
      centerAnchor: true,
      canHandleEvents: true,
      width: 327,
      height: 327,
      scale: 0.46,
      x: 610,
      y: 30,
      zIndex: 1,
    });

    // Panel's used for reward display
    this.panelWood = new ChestRewardsTooltip({
      superview: wood,
      width: 220,
      height: 180,
      scale: 2.5,
      zIndex: 2,
      x: wood.style.width / 2,
      y: -140,
      centerOnOrigin: true,
      visible: false,
      tracking: {
        feature: Feature.TREASURE_EXPRESS._,
        $subFeature: Feature.TREASURE_EXPRESS.CHEST_REWARDS,
      },
      parentSuperview: this.getSuperview()!,
    });
    wood.addListener('InputSelect', () => {
      this.panelWood.showRewards();
    });
    this.panelWood.setRewards(rewards[0]);

    this.panelSilver = new ChestRewardsTooltip({
      superview: silver,
      width: 220,
      height: 180,
      scale: 2.5,
      zIndex: 2,
      x: silver.style.width / 2,
      y: -140,
      centerOnOrigin: true,
      visible: false,
      tracking: {
        feature: Feature.TREASURE_EXPRESS._,
        $subFeature: Feature.TREASURE_EXPRESS.CHEST_REWARDS,
      },
      parentSuperview: this.getSuperview()!,
    });
    silver.addListener('InputSelect', () => {
      this.panelSilver.showRewards();
    });
    this.panelSilver.setRewards(rewards[1]);

    this.panelGold = new ChestRewardsTooltip({
      superview: gold,
      width: 220,
      height: 180,
      scale: 2.5,
      zIndex: 2,
      x: gold.style.width / 2,
      y: -140,
      centerOnOrigin: true,
      visible: false,
      tracking: {
        feature: Feature.TREASURE_EXPRESS._,
        $subFeature: Feature.TREASURE_EXPRESS.CHEST_REWARDS,
      },
      parentSuperview: this.getSuperview()!,
    });
    gold.addListener('InputSelect', () => {
      this.panelGold.showRewards();
    });
    this.panelGold.setRewards(rewards[2]);

    this.on('progress', (value: number) => {
      // Get milestones
      const milestones = getEventMilestones(user(), eventId);

      // Get unclaimed Milestones
      const unclaimedMilestones = getUnclaimedMilestones(user(), now());
      const numberOfUnclaimedMilestones = unclaimedMilestones
        ? unclaimedMilestones[eventId]
        : 0;

      // Get milestones user has claimed
      const completed = getEventCompletedMilestones(user(), eventId);
      const milestoneRewardIndex = completed - numberOfUnclaimedMilestones;

      wood.updateOpts({
        chestOpen: value >= milestones[0] && milestoneRewardIndex >= 1,
        chestShine: value >= milestones[0] && milestoneRewardIndex < 1,
      });

      silver.updateOpts({
        chestOpen: value >= milestones[1] && milestoneRewardIndex >= 2,
        chestShine: value >= milestones[1] && milestoneRewardIndex < 2,
      });

      gold.updateOpts({
        chestOpen: value >= milestones[2] && milestoneRewardIndex >= 3,
        chestShine: value >= milestones[2] && milestoneRewardIndex < 3,
      });

      this.paddingRight =
        value >= milestones[2] && milestoneRewardIndex >= 3 ? 20 : 90;
    });
  }

  public get fillWidthRatio() {
    const max = (this.style.width ?? 497) - this.paddingRight;
    return this.progressFill.style.width / max;
  }
}
